import { ReactElement, useEffect, useMemo, useState } from "react";

import {
  Table,
  constants,
  useWindowSize,
  visuallyHiddenStyles,
} from "@newsela/angelou";

import PercentageBarGraph from "@/reports/components/PercentageBarGraph";
import { StandardCategory } from "@/types/models/StandardCategory.interface";
import { tableCss } from "./ReadingSkillsDataTable.style";
import TableHeaderCell from "./TableHeaderCell";

export interface TableDataValue {
  percentageValue: number | null;
  labelValue: number | null;
}

export interface TableData {
  [key: string]: TableDataValue | string;
}

interface TableColumn {
  accessor: string;
  Header?: (() => ReactElement) | string;
  Cell?: ({ cell }: TableCell) => ReactElement;
  fixed?: boolean;
}

interface TableCell {
  cell: {
    value: TableDataValue;
    row: {
      index: number;
      original: {
        [key: string]: string;
      };
    };
  };
}

interface TableRow {
  values: {
    [key: string]: TableDataValue;
  };
}

export interface ReactTableProps<T = TableData> {
  filters: {
    id: keyof T;
    value: unknown;
  }[];
  pageSize: number;
  pageIndex: number;
  globalFilter?: unknown;
  sortBy: {
    desc: boolean;
    id: keyof T;
  }[];
}

interface ReadingSkillsDataTableProps {
  filterByReadingSkillSlug?: StandardCategory["slug"] | null;
  firstColumn: TableColumn;
  headerCellLabel?: string;
  headerData?: TableData;
  renderDataCellLabel: (value: TableDataValue) => string;
  renderTitleSection: ({
    displaySidebar,
  }: {
    displaySidebar?: boolean;
  }) => ReactElement;
  tableData: TableData[];
  readingSkills: StandardCategory[];
  isLoading?: boolean;
  onControlsUpdate?: (reactTableProps: ReactTableProps) => void | undefined;
  initialState?: {
    pageSize: number;
    totalResults: number;
    pageCount: number;
  };
}

const ReadingSkillsDataTable = ({
  filterByReadingSkillSlug,
  firstColumn,
  headerCellLabel,
  headerData,
  renderDataCellLabel,
  renderTitleSection,
  tableData,
  readingSkills,
  initialState,
  onControlsUpdate,
  isLoading,
}: ReadingSkillsDataTableProps) => {
  const { width: windowWidth } = useWindowSize();
  const {
    breakpoints: {
      Breakpoints: { xs },
    },
  } = constants;
  const isMobile = windowWidth < xs;

  const sortTypeFn = (rowA: TableRow, rowB: TableRow, columnId: string) => {
    const rowAPercentageValue = Number(
      rowA?.values[columnId]?.percentageValue ?? -1,
    );
    const rowBPercentageValue = Number(
      rowB?.values[columnId]?.percentageValue ?? -1,
    );
    if (rowAPercentageValue > rowBPercentageValue) return 1;
    if (rowBPercentageValue > rowAPercentageValue) return -1;
    return 0;
  };

  const [readingSkillsColumns, setReadingSkillsColumns] = useState<
    TableColumn[]
  >([]);
  const [filteredReadingSkillsColumns, setFilteredReadingSkillsColumns] =
    useState<TableColumn[]>([]);

  useMemo(() => {
    if (readingSkills && readingSkills.length > 0) {
      const newReadingSkillsColumns = readingSkills.map((readingSkill) => {
        return {
          accessor: readingSkill.slug,
          Cell: ({ cell }: TableCell) => (
            <PercentageBarGraph
              value={
                cell.value?.labelValue === 0 || isLoading
                  ? null
                  : cell.value?.percentageValue
              }
              label={
                isLoading ? "... quizzes" : renderDataCellLabel(cell.value)
              }
              isLoading={isLoading}
            />
          ),
          Header: () => (
            <TableHeaderCell
              data={headerData}
              readingSkill={readingSkill}
              label={headerCellLabel}
            />
          ),
          sortType: sortTypeFn,
        };
      });
      setReadingSkillsColumns(newReadingSkillsColumns);
      setFilteredReadingSkillsColumns(newReadingSkillsColumns);
    }
  }, [readingSkills]);

  useEffect(() => {
    if (!readingSkillsColumns) return;
    if (filterByReadingSkillSlug) {
      const filteredReadingSkillColumn = readingSkillsColumns.find(
        (column) => column.accessor === filterByReadingSkillSlug,
      );
      filteredReadingSkillColumn &&
        setFilteredReadingSkillsColumns([filteredReadingSkillColumn]);
    } else {
      setFilteredReadingSkillsColumns(readingSkillsColumns);
    }
  }, [filterByReadingSkillSlug, readingSkillsColumns]);

  const columns = [
    {
      ...firstColumn,
      fixed: !isMobile,
    },
    ...filteredReadingSkillsColumns,
  ];

  return (
    <div>
      <div role="status" css={visuallyHiddenStyles}>
        {isLoading ? "Loading reading skills table data..." : ""}
      </div>
      <Table
        __cssFor={tableCss}
        __renderTitle={renderTitleSection}
        columns={columns}
        data={tableData}
        useManualControls
        pageCount={initialState ? initialState.pageCount : 0}
        totalItems={initialState ? initialState.totalResults : 0}
        onControlsUpdate={onControlsUpdate}
        initialState={{
          ...initialState,
        }}
        paginationOptions={[
          {
            value: 25,
            label: "25",
          },
          {
            value: 50,
            label: "50",
          },
          {
            value: 75,
            label: "75",
          },
        ]}
      />
    </div>
  );
};

export default ReadingSkillsDataTable;
